/**
 * Minimal offline queue manager for persisting form submissions when offline.
 */

interface QueuedAction {
  id: string
  url: string
  options: RequestInit
  timestamp: number
  retries: number
}

const STORAGE_KEY = 'offlineActionQueue'
const MAX_RETRIES = 3

class OfflineQueueManager {
  private queue: QueuedAction[] = []

  constructor() {
    this.loadQueue()
  }

  private loadQueue() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        this.queue = JSON.parse(stored)
      }
    } catch {
      this.queue = []
    }
  }

  private saveQueue() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.queue))
    } catch {
      // Ignore
    }
  }

  add(url: string, options: RequestInit): string {
    const id = `action-${Date.now()}-${Math.random()}`
    const action: QueuedAction = {
      id,
      url,
      options,
      timestamp: Date.now(),
      retries: 0,
    }
    this.queue.push(action)
    this.saveQueue()
    return id
  }

  async processQueue(): Promise<{ succeeded: number; failed: number }> {
    if (this.queue.length === 0) return { succeeded: 0, failed: 0 }

    const results = { succeeded: 0, failed: 0 }
    const remaining: QueuedAction[] = []

    for (const action of this.queue) {
      try {
        const response = await fetch(action.url, action.options)
        if (response.ok) {
          results.succeeded++
        } else {
          action.retries++
          if (action.retries < MAX_RETRIES) {
            remaining.push(action)
          } else {
            results.failed++
          }
        }
      } catch {
        action.retries++
        if (action.retries < MAX_RETRIES) {
          remaining.push(action)
        } else {
          results.failed++
        }
      }
    }

    this.queue = remaining
    this.saveQueue()
    return results
  }

  getQueueSize(): number {
    return this.queue.length
  }
}

export const offlineQueue = new OfflineQueueManager()